import Icon from '@/components/ui/AppIcon';

interface Limitation {
  title: string;
  description: string;
  iconName: string;
}

interface LimitationsSectionProps {
  className?: string;
}

const LimitationsSection = ({ className = '' }: LimitationsSectionProps) => {
  const limitations: Limitation[] = [
    {
      title: "No Creative Services",
      description: "We don't design ads, write copy, or produce video content. You bring your creative assets, we provide the infrastructure to run them compliantly.",
      iconName: "XCircleIcon"
    },
    {
      title: "No Strategy Consulting",
      description: "We won't tell you which audiences to target or which campaigns to run. Strategic decisions remain entirely with you - we execute your strategy through our infrastructure.",
      iconName: "XCircleIcon"
    },
    {
      title: "No Performance Management",
      description: "We don't optimize your campaigns, adjust bids, or manage budgets. You control all optimization decisions through our platform access.",
      iconName: "XCircleIcon"
    },
    {
      title: "No Beginner Support",
      description: "We don't provide educational content, basic training, or hand-holding. Our service is designed for professional advertisers who know what they're doing.",
      iconName: "XCircleIcon"
    },
    {
      title: "No Performance Guarantees",
      description: "We never promise ROAS, conversion rates, or growth metrics. We provide reliable infrastructure - results depend on your execution.",
      iconName: "XCircleIcon"
    },
    {
      title: "No Reporting Analytics",
      description: "We don't build custom dashboards or provide performance analysis. You access platform data directly through our infrastructure.",
      iconName: "XCircleIcon"
    }
  ];

  return (
    <section className={`py-20 lg:py-28 bg-surface ${className}`}>
      <div className="mx-auto px-6 lg:px-12 max-w-7xl">
        <div className="max-w-3xl mb-16">
          <h2 className="font-headline font-bold text-3xl lg:text-4xl text-primary mb-6">
            What We Don't Do
          </h2>
          <p className="font-body text-lg text-text-secondary leading-relaxed">
            Transparency about our limitations is as important as showcasing our strengths. Here's what you won't get from Danny Clans Media - and why that's actually an advantage.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {limitations.map((item, index) => (
            <div
              key={index}
              className="bg-card p-6 rounded-sm border border-border"
            >
              <div className="flex items-start space-x-3 mb-3">
                <Icon
                  name={item.iconName as any}
                  size={24}
                  variant="solid"
                  className="text-destructive flex-shrink-0 mt-1"
                />
                <h3 className="font-headline font-semibold text-lg text-primary">
                  {item.title}
                </h3>
              </div>
              <p className="font-body text-sm text-text-secondary leading-relaxed">
                {item.description}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-12 p-8 bg-success/10 border border-success/20 rounded-sm">
          <div className="flex items-start space-x-4">
            <Icon
              name="LightBulbIcon"
              size={28}
              variant="solid"
              className="text-success flex-shrink-0 mt-1"
            />
            <div>
              <h4 className="font-headline font-semibold text-xl text-primary mb-3">
                Why Limitations Are Our Strength
              </h4>
              <p className="font-body text-base text-text-secondary leading-relaxed mb-4">
                By explicitly defining what we don't do, we can focus entirely on what we do best: building and maintaining institutional-grade advertising infrastructure. This narrow focus allows us to deliver reliability that full-service agencies cannot match.
              </p>
              <p className="font-body text-base text-text-secondary leading-relaxed">
                When you work with Danny Clans Media, you get world-class infrastructure without the overhead, complexity, or conflicts of interest that come with full-service agency relationships.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="p-6 bg-card border border-border rounded-sm">
            <h4 className="font-headline font-semibold text-lg text-primary mb-3 flex items-center space-x-2">
              <Icon name="UserGroupIcon" size={20} variant="outline" className="text-accent" />
              <span>Ideal Client Profile</span>
            </h4>
            <ul className="space-y-2">
              <li className="font-body text-sm text-text-secondary flex items-start space-x-2">
                <span className="text-success mt-1">•</span>
                <span>Professional media buyers with proven track records</span>
              </li>
              <li className="font-body text-sm text-text-secondary flex items-start space-x-2">
                <span className="text-success mt-1">•</span>
                <span>Agencies needing compliant infrastructure for high-risk verticals</span>
              </li>
              <li className="font-body text-sm text-text-secondary flex items-start space-x-2">
                <span className="text-success mt-1">•</span>
                <span>Enterprise advertisers requiring white-label platform access</span>
              </li>
              <li className="font-body text-sm text-text-secondary flex items-start space-x-2">
                <span className="text-success mt-1">•</span>
                <span>Performance marketers who optimize their own campaigns</span>
              </li>
            </ul>
          </div>

          <div className="p-6 bg-card border border-border rounded-sm">
            <h4 className="font-headline font-semibold text-lg text-primary mb-3 flex items-center space-x-2">
              <Icon name="XCircleIcon" size={20} variant="outline" className="text-destructive" />
              <span>Not Right For</span>
            </h4>
            <ul className="space-y-2">
              <li className="font-body text-sm text-text-secondary flex items-start space-x-2">
                <span className="text-destructive mt-1">•</span>
                <span>Businesses seeking full-service marketing support</span>
              </li>
              <li className="font-body text-sm text-text-secondary flex items-start space-x-2">
                <span className="text-destructive mt-1">•</span>
                <span>Advertisers needing creative or strategy guidance</span>
              </li>
              <li className="font-body text-sm text-text-secondary flex items-start space-x-2">
                <span className="text-destructive mt-1">•</span>
                <span>Clients expecting performance guarantees or ROAS promises</span>
              </li>
              <li className="font-body text-sm text-text-secondary flex items-start space-x-2">
                <span className="text-destructive mt-1">•</span>
                <span>Beginners requiring educational support or training</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LimitationsSection;